/**
 * Field-level network-output views fronting `NetworkOutputs_Field`.
 *
 * Every measure surface on the source — the scenario rate families and the
 * calculated-totals, well-solver-match and flowline-flag concerns — carries
 * per-row, run-indexed timeseries handles, so each is a timestamp-aligned,
 * wide-typed field-grain view: `createAlignedSeriesView` reads every measure
 * handle, outer-joins them on the run timestamp, and emits one row per
 * `(field × run timestamp)` with each measure a typed scalar column carrying its
 * unit, a `timestamp` column, and identity + the `fieldEntity` direct-relation
 * broadcast onto every row.
 *
 * The scenario rate families map over the shared `SCENARIO_FAMILIES` SSOT via
 * `buildScenarioFamilyViews`: the field grain extends `SCENARIO_BASE` with the
 * system-optimized gas-lift/power extras and the ad-hoc operator annotations.
 *
 * The aligned views close over the per-session CDF client, so the producer is a
 * hook.
 */

import { createAlignedSeriesView } from '../createAlignedSeriesView.ts';
import type { VirtualView } from '../createReshapedView.ts';
import {
  ADHOC_ANNOTATIONS,
  col,
  FIELD_CALC_TOTALS,
  FIELD_FLOWLINE_FLAGS,
  FIELD_SYSTEM_OPT_EXTRAS,
  FIELD_WELL_SOLVER_MATCH,
  IDENTITY,
  SCENARIO_BASE,
  type Shape,
} from '../shapes/index.ts';
import { buildScenarioFamilyViews, type ScenarioFamily, type ScenarioKind } from './optimizationScenarios.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import { NetworkOutputsFieldView } from '#schema/generated/ipso/views.ts';

/**
 * Identity + the field direct-relation kept on every NetworkOutputs_Field view.
 * The base `NetworkOutputs_Field` source (used here for its run-id columns) names
 * the grain ref `entityName`, so it is renamed to the canonical `fieldEntity`.
 */
const FIELD_KEEP: Shape = [
  ...IDENTITY,
  col('fieldEntity', 'unitless', 'Reference to the field model entity.', { suffix: 'entityName' }),
];

/**
 * The field grain's per-kind measure shape, layered on the shared
 * `SCENARIO_BASE` (phase rates + run id): the system-optimized families add the
 * gas-lift-rate + power-draw extras; the ad-hoc families add the operator
 * comment + executed-by annotations; the standard families carry the base only.
 */
export const FIELD_SCENARIO_SHAPE_BY_KIND: Record<ScenarioKind, Shape> = {
  standard: SCENARIO_BASE,
  systemOpt: [...SCENARIO_BASE, ...FIELD_SYSTEM_OPT_EXTRAS],
  adHocOpt: [...SCENARIO_BASE, ...ADHOC_ANNOTATIONS],
  adHocSim: [...SCENARIO_BASE, ...ADHOC_ANNOTATIONS],
};

/**
 * One non-scenario concern surface on `NetworkOutputs_Field`: its registry
 * name, description and the bare timeseries-handle measure group it aligns.
 */
type FieldConcernSpec = ViewFamilySpec & {
  /** The bare timeseries-handle measure group this view aligns (`suffix === canonical`). */
  readonly shape: Shape;
};

/**
 * The three concern surfaces over the FIELD grain: calculated
 * joint/separator/total rates + pressures, well-solver match diagnostics, and
 * the flowline flow-assurance flags. All bare canonical timeseries handles — no
 * rename, no prefix — aligned wide by run timestamp.
 */
const FIELD_CONCERN_SPECS: readonly FieldConcernSpec[] = [
  {
    name: 'FieldCalculatedTotals',
    description:
      'Network-solve calculated rates and pressures rolled up to the FIELD grain, aligned wide by run timestamp: total/joint/separator oil/gas/water/liquid rates, gas- and water-injection totals, commingling-joint pressure + CO2/N2/H2S fractions, flowline pressure, and the pressure match-quality index. Filter by fieldEntity (or join the field entity) to scope to a field.',
    shape: FIELD_CALC_TOTALS,
  },
  {
    name: 'FieldWellSolverMatch',
    description:
      'Field-rollup well-solver match diagnostics (FIELD grain), aligned wide by run timestamp: solver measured vs estimated total oil/gas/water/liquid rates, the per-phase measured-vs-estimated differences, and the per-phase flow match-quality indices. Filter by fieldEntity (or join the field entity) to scope to a field.',
    shape: FIELD_WELL_SOLVER_MATCH,
  },
  {
    name: 'FieldFlowAssuranceFlags',
    description:
      'Estimated flowline flow-assurance condition flags rolled up to the FIELD grain, aligned wide by run timestamp: the unstable-flow flag and the flow-assurance risk flag. Filter by fieldEntity (or join the field entity) to scope to a field.',
    shape: FIELD_FLOWLINE_FLAGS,
  },
];

/**
 * Build the nine per-scenario field-grain aligned views from the shared
 * `SCENARIO_FAMILIES` registry (including both the system-optimized and
 * system-optimized-choke families). Each aligns its scenario-prefixed measures
 * into typed per-measure value columns + a `timestamp` column over the FIELD
 * grain, broadcasting identity + the `fieldEntity` relation onto every aligned
 * row; the two ad-hoc simulation views classify a `scenarioType` enum from their
 * `comment` column.
 */
const useFieldScenarioRateViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return buildScenarioFamilyViews({
    grainStem: 'Field',
    source: NetworkOutputsFieldView,
    keep: FIELD_KEEP,
    shapeByKind: FIELD_SCENARIO_SHAPE_BY_KIND,
    describe: (family: ScenarioFamily) =>
      `Field-grain ${family.label} oil/gas/water production rates aligned wide by run timestamp: one row per (field × run timestamp), each rate a typed value column. Filter by fieldEntity (or join the field entity) to scope to a field.`,
    client,
    mintSeries,
  });
};

/** Build the three field-grain concern aligned views. */
const useFieldConcernViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return FIELD_CONCERN_SPECS.map((spec: FieldConcernSpec) =>
    createAlignedSeriesView({
      name: spec.name,
      description: spec.description,
      source: NetworkOutputsFieldView,
      keep: FIELD_KEEP,
      groups: { shape: spec.shape },
      client,
      mintSeries,
    }),
  );
};

/**
 * Build every FIELD-grain network-output surface fronting
 * `NetworkOutputs_Field`: the nine scenario rate views plus the three
 * concern views, all timestamp-aligned and wide-typed, closing over the
 * per-session CDF client.
 */
export const useNetworkOutputsFieldViews = (): readonly VirtualView[] => [
  ...useFieldScenarioRateViews(),
  ...useFieldConcernViews(),
];
